"use client";

import { c as _c } from "react/compiler-runtime";
import { SearchLg as SearchIcon } from "@untitledui/icons";
import type { FocusEventHandler, PointerEventHandler, RefAttributes, RefObject } from "react";
import { useCallback, use, useRef, useState } from "react";
import type { ComboBoxProps as AriaComboBoxProps, GroupProps as AriaGroupProps, ListBoxProps as AriaListBoxProps } from "react-aria-components";
import { ComboBox as AriaComboBox, Group as AriaGroup, Input as AriaInput, ListBox as AriaListBox, ComboBoxStateContext } from "react-aria-components";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { Popover } from "@/components/base/select/popover";
import { type CommonProps, SelectContext, type SelectItemType, sizes } from "@/components/base/select/select-shared";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { cx } from "@/utils/cx";
interface ComboBoxProps extends Omit<AriaComboBoxProps<SelectItemType>, "children" | "items">, RefAttributes<HTMLDivElement>, CommonProps {
  shortcut?: boolean;
  items?: SelectItemType[];
  popoverClassName?: string;
  shortcutClassName?: string;
  children: AriaListBoxProps<SelectItemType>["children"];
}
interface ComboBoxValueProps extends AriaGroupProps {
  size: "sm" | "md" | "lg";
  shortcut: boolean;
  placeholder?: string;
  shortcutClassName?: string;
  onFocus?: FocusEventHandler;
  onPointerEnter?: PointerEventHandler;
  ref?: RefObject<HTMLDivElement | null>;
}
const ComboBoxValue = t0 => {
  const $ = _c(24);
  let otherProps;
  let placeholder;
  let shortcut;
  let shortcutClassName;
  let size;
  if ($[0] !== t0) {
    ({
      size,
      shortcut,
      placeholder,
      shortcutClassName,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = otherProps;
    $[2] = placeholder;
    $[3] = shortcut;
    $[4] = shortcutClassName;
    $[5] = size;
  } else {
    otherProps = $[1];
    placeholder = $[2];
    shortcut = $[3];
    shortcutClassName = $[4];
    size = $[5];
  }
  const state = use(ComboBoxStateContext);
  const value = state?.selectedItem?.value || null;
  const inputValue = state?.inputValue || null;
  let first;
  let t1;
  if ($[6] !== inputValue || $[7] !== value?.supportingText) {
    first = inputValue?.split(value?.supportingText)?.[0] || "";
    t1 = inputValue?.split(first)[1];
    $[6] = inputValue;
    $[7] = value?.supportingText;
    $[8] = first;
    $[9] = t1;
  } else {
    first = $[8];
    t1 = $[9];
  }
  const last = t1;
  let t2;
  if ($[10] !== size) {
    t2 = t3 => {
      const {
        isFocusWithin,
        isDisabled
      } = t3;
      return cx("relative flex w-full items-center gap-2 rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition-shadow duration-100 ease-linear ring-inset", isDisabled && "cursor-not-allowed bg-disabled_subtle", isFocusWithin && "ring-2 ring-brand", sizes[size].root);
    };
    $[10] = size;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== first || $[13] !== inputValue || $[14] !== last || $[15] !== placeholder || $[16] !== shortcut || $[17] !== shortcutClassName || $[18] !== size) {
    t3 = t4 => {
      const {
        isDisabled: isDisabled_0
      } = t4;
      return <><SearchIcon className="pointer-events-none size-5 text-fg-quaternary" /><div className="relative flex w-full items-center gap-2">{inputValue && <span className="absolute top-1/2 z-0 inline-flex w-full -translate-y-1/2 gap-2 truncate" aria-hidden="true"><p className={cx("text-md font-medium text-primary", isDisabled_0 && "text-disabled")}>{first}</p>{last && <p className={cx("-ml-0.75 text-md text-tertiary", isDisabled_0 && "text-disabled")}>{last}</p>}</span>}<AriaInput placeholder={placeholder} className="z-10 w-full appearance-none bg-transparent text-md text-transparent caret-alpha-black/90 placeholder:text-placeholder focus:outline-hidden disabled:cursor-not-allowed disabled:text-disabled disabled:placeholder:text-disabled" /></div>{shortcut && <div className={cx("absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8", isDisabled_0 && "to-bg-disabled_subtle", sizes[size].shortcut, shortcutClassName)}><span className={cx("pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset", isDisabled_0 && "bg-transparent text-disabled")} aria-hidden="true">⌘K</span></div>}</>;
    };
    $[12] = first;
    $[13] = inputValue;
    $[14] = last;
    $[15] = placeholder;
    $[16] = shortcut;
    $[17] = shortcutClassName;
    $[18] = size;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== otherProps || $[21] !== t2 || $[22] !== t3) {
    t4 = <AriaGroup {...otherProps} className={t2}>{t3}</AriaGroup>;
    $[20] = otherProps;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  return t4;
};
export const ComboBox = t0 => {
  const $ = _c(30);
  let children;
  let items;
  let otherProps;
  let shortcutClassName;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      placeholder: t1,
      shortcut: t2,
      size: t3,
      children,
      items,
      shortcutClassName,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = items;
    $[3] = otherProps;
    $[4] = shortcutClassName;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    children = $[1];
    items = $[2];
    otherProps = $[3];
    shortcutClassName = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  const placeholder = t1 === undefined ? "Search" : t1;
  const shortcut = t2 === undefined ? true : t2;
  const size = t3 === undefined ? "sm" : t3;
  const placeholderRef = useRef(null);
  const [popoverWidth, setPopoverWidth] = useState("");
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (!placeholderRef.current) {
        return;
      }
      const divRect = placeholderRef.current?.getBoundingClientRect();
      setPopoverWidth(divRect.width + "px");
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const onResize = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      ref: placeholderRef,
      box: "border-box",
      onResize
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useResizeObserver(t5);
  let t6;
  if ($[10] !== size) {
    t6 = {
      size
    };
    $[10] = size;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children || $[13] !== items || $[14] !== otherProps.hint || $[15] !== otherProps.label || $[16] !== otherProps.popoverClassName || $[17] !== otherProps.tooltip || $[18] !== placeholder || $[19] !== popoverWidth || $[20] !== shortcut || $[21] !== shortcutClassName || $[22] !== size) {
    t7 = state => <div className="flex flex-col gap-1.5">{otherProps.label && <Label isRequired={state.isRequired} tooltip={otherProps.tooltip}>{otherProps.label}</Label>}<ComboBoxValue ref={placeholderRef} placeholder={placeholder} shortcut={shortcut} shortcutClassName={shortcutClassName} size={size} onFocus={onResize} onPointerEnter={onResize} /><Popover size={size} triggerRef={placeholderRef} style={{
        width: popoverWidth
      }} className={otherProps.popoverClassName}><AriaListBox items={items} className="size-full outline-hidden">{children}</AriaListBox></Popover>{otherProps.hint && <HintText isInvalid={state.isInvalid}>{otherProps.hint}</HintText>}</div>;
    $[12] = children;
    $[13] = items;
    $[14] = otherProps.hint;
    $[15] = otherProps.label;
    $[16] = otherProps.popoverClassName;
    $[17] = otherProps.tooltip;
    $[18] = placeholder;
    $[19] = popoverWidth;
    $[20] = shortcut;
    $[21] = shortcutClassName;
    $[22] = size;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== otherProps || $[25] !== t7) {
    t8 = <AriaComboBox menuTrigger="focus" {...otherProps}>{t7}</AriaComboBox>;
    $[24] = otherProps;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t6 || $[28] !== t8) {
    t9 = <SelectContext.Provider value={t6}>{t8}</SelectContext.Provider>;
    $[27] = t6;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};